import { Ticket, TicketGroup } from '../../model/product';

/**
 * 검수 게이트가 "무엇을 고쳐야 하는가"를 계산하는 순수 로직. 컴포넌트에서 분리한 이유는 판정이
 * 서버 게이트와 어긋나면 안내와 차단이 서로 다른 상품을 가리키기 때문이다(NNB-679) — 그 정합성을
 * HTTP 없이 테스트로 고정한다.
 */

/** 회차가 지금 예약을 받을 수 있는지. NestJS `FUTURE_OPEN_TICKET_CONDITIONS` 와 같은 술어다. */
export function isOpenTicket(ticket: Ticket, reservationHours: number, now: Date): boolean {
  const cutoff = now.getTime() + reservationHours * 60 * 60 * 1000;
  const date = new Date(ticket.date).getTime();
  if (!Number.isFinite(date) || date <= cutoff) return false;
  return (
    !!ticket.isActivated && !ticket.isDeactivated && !ticket.isHidden && !ticket.isClosed && !ticket.isReservationEnd
  );
}

/** 판매 상태를 켜면 달라지는 일정. 그룹 단위 토글이라 그룹이 선택 단위다. */
export interface OffScheduleGroup {
  id: number;
  name: string;
  from: Date;
  /** 그룹 기간 종료일. 서버에서 nullable 이라 없을 수 있다. */
  to: Date | null;
  futureTicketCount: number;
}

/** 판매 상태를 켜도 막혀 있는 회차. 호스트가 직접 마감한 것만 회차 단위로 풀 수 있다. */
export interface BlockedTicket {
  id: number;
  date: string;
  groupId: number;
}

/**
 * 휴무는 상품·날짜 단위로만 풀린다(`setCloseTicket` 이 그 KST 하루 전체를 연다). 회차 하나를 고르게
 * 하면 고르지 않은 같은 날 회차까지 열려 화면이 사실과 다른 말을 하므로, 선택 단위를 날짜로 묶는다.
 */
export interface HolidayDate {
  /** `setCloseTicket` 에 넘길 대표 시각 — 같은 KST 날짜면 어느 회차든 결과가 같다. */
  sample: string;
  /** `YYYY-MM-DD` (KST). 중복 제거 키이자 라벨 근거. */
  kstDate: string;
  ticketCount: number;
}

export interface GateModel {
  /** 1단계 대상 — 판매 상태가 꺼진 미래 일정 */
  offGroups: OffScheduleGroup[];
  /** 2단계 대상 — 호스트가 직접 마감한 회차 */
  blockedTickets: BlockedTicket[];
  /** 2단계 대상 — 휴무로 지정된 날짜 */
  holidayDates: HolidayDate[];
  /** 미래 회차가 아예 없는지. 「일정을 등록해주세요」와 「이 일정을 고쳐주세요」를 가른다. */
  hasFutureTicket: boolean;
}

/**
 * 열린 회차가 하나도 없는 그룹만 "고칠 대상"으로 추린다 — 서버 게이트가 보는 것과 같은 기준이다.
 * `activatedTicketCount` 를 쓰지 않는 이유: 서버가 `loadRelationCountAndMap` 으로 채워 날짜·숨김
 * 필터를 물려받지 않으므로, 과거에 팔았던 그룹이 통째로 빠져 "일정을 등록해주세요"가 된다.
 */
export function buildGateModel(
  groups: TicketGroup[],
  tickets: Ticket[],
  reservationHours: number,
  now: Date,
): GateModel {
  const byGroup = new Map<number, Ticket[]>();
  for (const ticket of tickets) {
    const groupId = ticket.group?.id;
    if (groupId == null) continue;
    const bucket = byGroup.get(groupId);
    if (bucket) bucket.push(ticket);
    else byGroup.set(groupId, [ticket]);
  }

  const offGroups: OffScheduleGroup[] = [];
  const blockedTickets: BlockedTicket[] = [];
  const holidayByDate = new Map<string, HolidayDate>();
  let hasFutureTicket = false;

  for (const group of groups) {
    const groupId = group.id;
    if (groupId == null) continue;
    const owned = byGroup.get(groupId) ?? [];
    const future = owned.filter((ticket) => isFuture(ticket, now));
    if (future.length === 0) continue;
    hasFutureTicket = true;
    if (future.some((ticket) => isOpenTicket(ticket, reservationHours, now))) continue;

    // 예약 마감 시간 안에 든 회차는 켜도 마감을 풀어도 열리지 않는다. 고칠 수 있는 회차만 사유로
    // 모아야 "고쳤습니다 → 검수 요청" 뒤에 서버가 또 막는 일이 없다.
    const fixable = future.filter((ticket) => isAfterCutoff(ticket, reservationHours, now));
    if (fixable.length === 0) continue;

    if (fixable.some((ticket) => !ticket.isActivated || ticket.isDeactivated)) {
      offGroups.push({
        id: groupId,
        name: group.name,
        from: new Date(group.from),
        to: toValidDate(group.to),
        futureTicketCount: fixable.length,
      });
    }
    for (const ticket of fixable) {
      if (ticket.isReservationEnd) {
        blockedTickets.push({ id: ticket.id, date: ticket.date, groupId });
      }
      if (ticket.isClosed) {
        const kstDate = toKstDate(ticket.date);
        const existing = holidayByDate.get(kstDate);
        if (existing) existing.ticketCount += 1;
        else holidayByDate.set(kstDate, { sample: ticket.date, kstDate, ticketCount: 1 });
      }
    }
  }

  return {
    offGroups,
    blockedTickets,
    holidayDates: [...holidayByDate.values()].sort((a, b) => a.kstDate.localeCompare(b.kstDate)),
    hasFutureTicket,
  };
}

/** 열어야 할 것이 하나도 없으면 2단계를 아예 띄우지 않는다. */
export function hasBlockedWork(model: GateModel): boolean {
  return model.blockedTickets.length > 0 || model.holidayDates.length > 0;
}

/**
 * 호스트가 실제로 거치는 단계 수. 켤 일정이 없으면 1단계를 건너뛰므로(판매중인데 전부 마감인 상품)
 * 총수에서도 빠져야 "3단계 중 1단계" 다음에 바로 끝나는 일이 없다.
 */
export function countSteps(model: GateModel): number {
  return 1 + (model.offGroups.length > 0 ? 1 : 0) + (hasBlockedWork(model) ? 1 : 0);
}

/** 예약 마감 시간을 넘겼는지. 넘기지 못한 회차는 호스트가 고칠 수 있는 대상이 아니다. */
function isAfterCutoff(ticket: Ticket, reservationHours: number, now: Date): boolean {
  const date = new Date(ticket.date).getTime();
  return Number.isFinite(date) && date > now.getTime() + reservationHours * 60 * 60 * 1000;
}

function isFuture(ticket: Ticket, now: Date): boolean {
  const date = new Date(ticket.date).getTime();
  return Number.isFinite(date) && date > now.getTime();
}

/** `to` 는 nullable 이고 JSON 역직렬화 값이라 타입이 보장하지 않는다 — Invalid Date 를 흘리지 않는다. */
function toValidDate(value: Date | string | null | undefined): Date | null {
  if (value == null) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

/** KST 기준 달력 날짜. 휴무가 하루 단위라 UTC 로 자르면 자정 전후 회차가 다른 날로 갈린다. */
export function toKstDate(value: string): string {
  const kst = new Date(new Date(value).getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
}
